import { Dataset, DtFilter, DtSort } from '@/components/data-table/helpers';
import { downloadFile, JsonObject } from '../helpers';
import { axiosClient as spApiClient, create, deleteApi, get, patch, post } from './sp-api.service';
import { TelemetryModel } from '../models/telemetry/telemetry.model';
import { CreateResponse, DEFAULT_SERVICE_ERROR, DeleteResponse, PatchResponse } from '.';
import { AxiosError, AxiosProgressEvent, isCancel } from 'axios';
import { orderBy } from 'lodash';
import { getTypedTelemetry } from '../models/telemetry';
import { getSearchFilter } from './user-event.service';

type TelemetryListResponse = {
  count: number;
  currentPage: number;
  data: JsonObject[];
  hasNext: boolean;
  hasPrevious: boolean;
  pageSize: number;
  totalPages: number;
};

export type TelemetryTestReport = {
  accessVerified: boolean;
  found: boolean;
  canWrite: boolean;
  messages: string[];
};

export type TelemetrySchemaVersion = {
  version: string;
  deprecationDate?: string;
  s3Prefix?: string;
};

export type TelemetrySettings = {
  destinations: {
    schemaVersions: {
      userEvents: {
        latest: TelemetrySchemaVersion;
        supported: TelemetrySchemaVersion[];
      };
      auditEvents: {
        latest: TelemetrySchemaVersion;
        supported: TelemetrySchemaVersion[];
      };
    };
  };
};

type LegacyTelemetrySettings = {
  destinations: {
    schemaVersions: {
      latest: TelemetrySchemaVersion;
      supported: TelemetrySchemaVersion[];
    };
  };
};

export const getSettings = async (): Promise<TelemetrySettings> => {
  const response = (await get('/telemetry/settings')) as JsonObject | null;
  if (!response) {
    return {
      destinations: {
        schemaVersions: {
          userEvents: {
            latest: { version: 'unknown' },
            supported: [],
          },
          auditEvents: {
            latest: { version: 'unknown' },
            supported: [],
          },
        },
      },
    };
  }

  const settings = response as TelemetrySettings;

  // legacy schema support
  if (!settings.destinations.schemaVersions.auditEvents) {
    const legacyResponse = response as LegacyTelemetrySettings;

    return {
      destinations: {
        schemaVersions: {
          userEvents: legacyResponse.destinations.schemaVersions,
          auditEvents: {
            latest: { version: 'unknown' },
            supported: [],
          },
        },
      },
    };
  }

  return settings;
};

export const getTelemetry = async (
  page: number,
  pageSize: number,
  dtSort?: DtSort,
  dtFilter?: DtFilter
): Promise<Dataset<TelemetryModel>> => {
  const result = (await get('/telemetry/telemetry-destinations', {
    page: 1,
    pageSize: 100,
  })) as TelemetryListResponse;

  if (!Array.isArray(result?.data)) {
    return {
      page,
      pageSize,
      rows: [],
      total: 0,
    };
  }

  let rows: TelemetryModel[] = result.data.map((data) =>
    getTypedTelemetry(new TelemetryModel(data))
  );

  if (dtFilter?.keyword) {
    const safeKeyword = String(dtFilter?.keyword).toLowerCase();
    rows = rows.filter(({ displayName }) => displayName.toLowerCase().includes(safeKeyword));
  }

  // Sort
  if (dtSort) {
    const { columnName, direction } = dtSort;
    rows = orderBy(rows, [columnName], [direction]);
  }

  // Paginate
  const total = rows.length;
  const startIndex = page * pageSize;
  rows = rows.slice(startIndex, startIndex + pageSize);

  return {
    page,
    pageSize,
    rows,
    total,
  };
};

export const getTelemetryById = async (telemetryId: string): Promise<TelemetryModel | null> => {
  const response = await get(`/telemetry/telemetry-destinations/${telemetryId}`);
  return response ? getTypedTelemetry(new TelemetryModel(response as JsonObject)) : null;
};

export const createTelemetry = async (telemetry: TelemetryModel): Promise<CreateResponse> => {
  const typedModel = getTypedTelemetry(telemetry);
  const { createModel } = typedModel;

  const response = await create(
    '/telemetry/telemetry-destinations',
    createModel._props,
    handleCreateErrorResponse
  );

  if (!response?.created) {
    return response;
  }

  const newModel = getTypedTelemetry(new TelemetryModel(response.data));
  response.model = newModel;

  await testTelemetry(newModel);

  return response;
};

export const patchTelemetry = async (telemetry: TelemetryModel): Promise<PatchResponse> => {
  const typedModel = getTypedTelemetry(telemetry);
  const { patchModel } = typedModel;

  const response = await patch(
    `/telemetry/telemetry-destinations/${telemetry.id}`,
    patchModel._props,
    handlePatchErrorResponse
  );

  if (!response?.patched) {
    return response;
  }

  await testTelemetry(telemetry);

  return response;
};

export const deleteTelemetry = async (telemetryId: string): Promise<DeleteResponse> => {
  return deleteApi(`/telemetry/telemetry-destinations/${telemetryId}`);
};

export const testTelemetry = async (
  telemetry: TelemetryModel
): Promise<TelemetryTestReport | null> => {
  const { id, canTest } = telemetry;

  if (!canTest) {
    return null;
  }

  const response = await post(`/telemetry/telemetry-destinations/${id}/verify-access`, {});

  const { report } = (response || {}) as { report: TelemetryTestReport };

  return report || null;
};

export const downloadTelemetry = async (
  dtFilter?: DtFilter,
  filename?: string,
  onDownloadProgress?: (event: AxiosProgressEvent) => void,
  controller?: AbortController
): Promise<{ aborted: boolean; downloaded: boolean }> => {
  const response = { aborted: false, downloaded: false };

  const filterParams = getSearchFilter(dtFilter);

  const result = await spApiClient
    .post(
      '/telemetry/activity/cursor',
      {
        sort: [{ ['startTime']: 'asc' }],
        match: filterParams,
      },
      {
        responseType: 'blob',
        timeout: 0,
        headers: { Accept: 'text/csv' },
        onDownloadProgress,
        signal: controller?.signal,
      }
    )
    .catch((err) => {
      if (isCancel(err)) {
        response.aborted = true;
      }
      console.error(err);
    });

  const blob = result?.data as Blob;

  if (!blob?.size) {
    return response;
  }

  const dateForFilename = new Date().toISOString().replace(/:/g, '-').replace(/\./g, '-');
  const downloadFilename =
    filename || `f5-workforce-ai-security-user-events-export-${dateForFilename}.csv`;

  downloadFile(blob, downloadFilename);

  response.downloaded = true;

  return response;
};

const handleCreateErrorResponse = (error: AxiosError): CreateResponse => {
  if (!error.response) {
    return { created: false, error: DEFAULT_SERVICE_ERROR };
  }

  const { status, data } = error.response as { status: number; data: JsonObject };

  if (status === 400 && Array.isArray(data?.errors)) {
    return {
      created: false,
      fieldErrors: data?.errors.map(({ field, rule }) => ({
        msg: rule as string,
        path: field as string,
        value: '',
      })),
    };
  }

  return { created: false, error: DEFAULT_SERVICE_ERROR };
};

const handlePatchErrorResponse = (error: AxiosError): PatchResponse => {
  if (!error.response) {
    return { patched: false, error: DEFAULT_SERVICE_ERROR };
  }

  const { status, data } = error.response as { status: number; data: JsonObject };

  if (status === 400 && Array.isArray(data?.errors)) {
    return {
      patched: false,
      fieldErrors: data?.errors.map(({ field, rule }) => ({
        msg: rule as string,
        path: field as string,
        value: '',
      })),
    };
  }

  return { patched: false, error: DEFAULT_SERVICE_ERROR };
};
